import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
 FileText,
 ScrollText,
 ArrowRight,
 Receipt,
 PenLine,
 Loader2,
} from 'lucide-react';
import { adminVendorDocumentsApi } from '../../../services/adminVendorDocumentsService';

/**
 * Hub cards linking to vendor invoices / contracts admin pages.
 */
const VendorDocumentsHub = ({ vendorUserId }) => {
 const { data, isLoading } = useQuery({
 queryKey: ['admin-vendor-documents-summary', vendorUserId],
 queryFn: () => adminVendorDocumentsApi.getSummary(vendorUserId),
 enabled: !!vendorUserId,
 });

 const summary = data?.data;
 const invoices = summary?.invoices;
 const contracts = summary?.contracts;

 const cards = [
 {
 key: 'invoices',
 title: 'Invoices',
 description: 'Client invoices and receipts this vendor issued.',
 to: `/admin/vendors/${vendorUserId}/invoices`,
 icon: FileText,
 tone: 'sky',
 total: invoices?.total,
 meta: invoices
 ? [
 { label: 'Paid', value: invoices.paid },
 { label: 'Open', value: invoices.sent },
 { label: 'Draft', value: invoices.draft },
 ]
 : [],
 },
 {
 key: 'contracts',
 title: 'Contracts',
 description: 'Agreements sent for client signature.',
 to: `/admin/vendors/${vendorUserId}/contracts`,
 icon: ScrollText,
 tone: 'emerald',
 total: contracts?.total,
 meta: contracts
 ? [
 { label: 'Signed', value: contracts.signed },
 { label: 'Sent', value: contracts.sent },
 { label: 'Draft', value: contracts.draft },
 ]
 : [],
 },
 ];

 const toneStyles = {
 sky: {
 icon: 'bg-surface-muted text-ink border-border',
 hover: 'hover:border-border ',
 },
 emerald: {
 icon: 'bg-emerald-50 text-emerald-700 border-emerald-100',
 hover: 'hover:border-emerald-300',
 },
 };

 return (
 <div className="space-y-3">
 <div className="flex items-center justify-between gap-3 px-0.5">
 <div>
 <h3 className="text-sm font-semibold text-ink">Documents</h3>
 <p className="text-xs text-ink-tertiary mt-0.5">
 Open a full list page to review invoices or contracts in detail.
 </p>
 </div>
 </div>

 {isLoading ? (
 <div className="product-panel p-8 flex items-center justify-center gap-2 text-sm text-gray-500">
 <Loader2 className="w-4 h-4 animate-spin text-ink" />
 Loading documents…
 </div>
 ) : (
 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
 {cards.map((card) => {
 const Icon = card.icon;
 const tone = toneStyles[card.tone] || toneStyles.sky;
 return (
 <Link
 key={card.key}
 to={card.to}
 className={`group product-panel p-5 shadow-sm transition-colors ${tone.hover}`}
 >
 <div className="flex items-start justify-between gap-3 mb-4">
 <div className={`flex h-10 w-10 items-center justify-center rounded-xl border ${tone.icon}`}>
 <Icon className="w-5 h-5" />
 </div>
 <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 group-hover:text-gray-800">
 Open
 <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
 </span>
 </div>
 <div className="flex items-baseline gap-2 mb-1">
 <h4 className="text-base font-semibold text-ink">{card.title}</h4>
 <span className="text-sm tabular-nums text-ink-tertiary">
 {card.total ?? 0}
 </span>
 </div>
 <p className="text-sm text-ink-tertiary mb-4">{card.description}</p>
 <div className="flex flex-wrap gap-2">
 {card.meta.map((m) => (
 <span
 key={m.label}
 className="inline-flex items-center gap-1.5 rounded-full bg-surface-muted border border-border px-2.5 py-1 text-[11px] text-ink-secondary"
 >
 {m.label === 'Paid' || m.label === 'Signed' ? (
 <Receipt className="w-3 h-3 text-emerald-500" />
 ) : m.label === 'Draft' ? (
 <PenLine className="w-3 h-3 text-gray-400" />
 ) : null}
 <span className="font-medium tabular-nums">{m.value}</span>
 <span className="text-gray-400">{m.label}</span>
 </span>
 ))}
 </div>
 </Link>
 );
 })}
 </div>
 )}
 </div>
 );
};

export default VendorDocumentsHub;
